/**
 * The return's second leg (ADR-040; the contract agreed with World, world-2d / World #2243): after Harry
 * writes `harry-to-world`, it says so on a same-origin BroadcastChannel, and a World tab that can take the
 * file answers. "Back to World" then knows whether a World tab already has it.
 *
 *   channel  `poqpoq-handoff`
 *   Harry →  { type: 'harry-export', v: 1, sha256, size, displayName, at, worldTab? }   — worldTab is World's
 *            nonce from world-to-harry (World #2280), copied unchanged; absent when there was none
 *   World →  { type: 'world-ack', v: 1, sha256 }   — only when World's signed-in user is the record's
 *            userId AND the sha matches the record it read. Several tabs may ack: the first whose sha
 *            matches one Harry announced counts.
 *
 * NOTHING on the channel is trusted beyond that match: any other message, field or shape is ignored.
 * OPTIONAL: a browser without BroadcastChannel, or one that throws, is a channel that never acks — the
 * return then behaves as it did before (navigate to World). Never a throw into the caller.
 *
 * Depends on: an injected channel factory. Depended on by: main.ts (the session's `announce`, Back to
 * World), tests.
 */

export const HANDOFF_CHANNEL = 'poqpoq-handoff';

/** The bit of BroadcastChannel this needs (injectable for tests). */
export interface ChannelLike {
  postMessage(message: unknown): void;
  addEventListener(type: 'message', listener: (event: { readonly data: unknown }) => void): void;
  close(): void;
}

export interface HarryExportMessage {
  readonly type: 'harry-export';
  readonly v: 1;
  readonly sha256: string;
  readonly size: number;
  readonly displayName: string;
  readonly at: string;
  /** World's tab nonce, copied unchanged from world-to-harry; absent when there was none (World #2280). */
  readonly worldTab?: string;
}

export interface WorldChannel {
  /** Say a record was written. Silent when the channel is unavailable. */
  readonly announce: (message: Omit<HarryExportMessage, 'type' | 'v'>) => void;
  /** Has a World tab acknowledged this sha? */
  readonly acked: (sha256: string) => boolean;
  /** An ack for this sha now, or within `ms` (the last announcement is repeated once, for a tab that opened late). */
  readonly waitAck: (sha256: string, ms: number) => Promise<boolean>;
  readonly close: () => void;
}

export function createWorldChannel(open: () => ChannelLike | null): WorldChannel {
  let channel: ChannelLike | null = null;
  try {
    channel = open();
  } catch {
    channel = null;
  }
  const announced = new Map<string, HarryExportMessage>();
  const acks = new Set<string>();
  const waiters = new Set<() => void>();
  channel?.addEventListener('message', (event) => {
    const d = event.data as { type?: unknown; v?: unknown; sha256?: unknown } | null;
    if (d === null || typeof d !== 'object' || d.type !== 'world-ack' || d.v !== 1 || typeof d.sha256 !== 'string') return;
    if (!announced.has(d.sha256)) return; // an ack for a file this tab never sent is nobody's business here
    acks.add(d.sha256);
    waiters.forEach((w) => w());
  });
  const post = (m: HarryExportMessage): void => {
    try {
      channel?.postMessage(m);
    } catch {
      /* optional channel: a refusal is the same as no channel */
    }
  };
  return {
    announce: (m) => {
      // Exactly the contract's fields — nothing else a caller passed rides along.
      const message: HarryExportMessage = { type: 'harry-export', v: 1, sha256: m.sha256, size: m.size, displayName: m.displayName, at: m.at, ...(m.worldTab !== undefined ? { worldTab: m.worldTab } : {}) };
      announced.set(m.sha256, message);
      post(message);
    },
    acked: (sha) => acks.has(sha),
    waitAck: (sha, ms) => {
      if (acks.has(sha)) return Promise.resolve(true);
      if (channel === null || !announced.has(sha)) return Promise.resolve(false);
      post(announced.get(sha)!);
      return new Promise((resolve) => {
        const done = (value: boolean): void => {
          waiters.delete(check);
          clearTimeout(timer);
          resolve(value);
        };
        const check = (): void => {
          if (acks.has(sha)) done(true);
        };
        const timer = setTimeout(() => done(false), ms);
        waiters.add(check);
      });
    },
    close: () => {
      try {
        channel?.close();
      } catch {
        /* nothing to do */
      }
      channel = null;
    },
  };
}
